import axios from "../../../services/axios/backendAxios";
import { RenderSiteMapState } from "../RenderSiteMap";

export interface ExportSiteMapParams {
  jobId: string;
  state: RenderSiteMapState;
  season: string;
  fileName?: string;
}

export function simplifyExportError(err: unknown): string {
  const errorObj = err as {
    message?: string;
    code?: string;
    status?: number;
    response?: { status?: number };
  };
  const msg = (errorObj?.message || "").toLowerCase();
  const code = (errorObj?.code || "").toUpperCase();
  const status = errorObj?.status || errorObj?.response?.status;

  if (status === 401 || status === 403) {
    return "You do not have permission to export this site map.";
  }
  if (status === 404) {
    return "Site map data could not be found. Please refresh and try again.";
  }
  if (status === 429) {
    return "Export limit reached. Please wait a few minutes before trying again.";
  }
  if (
    code === "MAP_RENDER_TIMEOUT" ||
    msg.includes("map rendering timed out") ||
    msg.includes("tiles took too long") ||
    msg.includes("timeout") ||
    msg.includes("timed out") ||
    msg.includes("time out") ||
    code === "ECONNABORTED"
  ) {
    return "The map rendering service took longer than expected to load the satellite imagery. The server attempted to reload the map, but the tiles could not finish rendering in time. Please try exporting again or contact support if the issue persists.";
  }
  if (
    msg.includes("network") ||
    msg.includes("econnrefused") ||
    msg.includes("failed to fetch")
  ) {
    return "Unable to reach the server. Please check your connection and try again.";
  }
  if (
    msg.includes("token") ||
    msg.includes("configure") ||
    msg.includes("not configured")
  ) {
    return "Site map export service is unavailable. Please contact support.";
  }
  return "Exporting image failed. Please try again later or contact support.";
}

const exportSiteMap = async ({
  jobId,
  state,
  season,
  fileName,
}: ExportSiteMapParams): Promise<void> => {
  try {
    const response = await axios.post(
      `job/${encodeURIComponent(jobId)}/export-sitemap`,
      {
        state: JSON.stringify(state),
        season,
        fileName: fileName ?? "",
      },
      {
        responseType: "blob",
      },
    );

    const blob = new Blob([response.data], {
      type: response.headers["content-type"] || "image/jpeg",
    });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    const downloadName =
      (fileName ? fileName.replace(/\.[^/.]+$/, "") : "sitemap") + ".jpg";
    link.setAttribute("download", downloadName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 1000);
  } catch (err: unknown) {
    const axiosError = err as {
      response?: { data?: Blob | { message?: string } };
      message?: string;
    };
    let message = "Could not render the site map image. Please try again.";
    let errorCode = (axiosError as { code?: string })?.code;
    const rawData = axiosError?.response?.data;
    if (
      rawData &&
      typeof rawData === "object" &&
      "text" in rawData &&
      typeof (rawData as { text: unknown }).text === "function"
    ) {
      try {
        const text = await (rawData as { text: () => Promise<string> }).text();
        const parsed = JSON.parse(text);
        if (parsed?.message) {
          message = parsed.message;
        }
        if (parsed?.code) {
          errorCode = parsed.code;
        }
      } catch {
        // Fall back to default message if text is not JSON
      }
    } else if (
      axiosError?.response?.data &&
      typeof axiosError.response.data === "object" &&
      "message" in axiosError.response.data
    ) {
      const dataObj = axiosError.response.data as {
        message?: string;
        code?: string;
      };
      if (typeof dataObj.message === "string") {
        message = dataObj.message;
      }
      if (typeof dataObj.code === "string") {
        errorCode = dataObj.code;
      }
    } else if (axiosError?.message) {
      message = axiosError.message;
    }
    const customError = new Error(message) as Error & {
      status?: number;
      code?: string;
    };
    customError.status = (
      axiosError as { response?: { status?: number } }
    )?.response?.status;
    customError.code = errorCode;
    throw customError;
  }
};

export default exportSiteMap;
